import { useState } from "react";
import {
  Search,
  Upload,
  Download,
  ChevronDown,
  ChevronRight,
  BookOpen,
  Zap,
  BarChart3,
  FileSpreadsheet,
  Music,
  HelpCircle,
  ArrowRight,
  CheckCircle2,
  Info,
} from "lucide-react";

/* ─── Screenshot component ─── */
const Screenshot = ({ src, alt }) => (
  <div className="my-4 rounded-xl overflow-hidden border border-white/10 shadow-lg shadow-black/20">
    <img
      src={src}
      alt={alt}
      className="w-full block"
      loading="lazy"
    />
    <div className="bg-white/[0.02] border-t border-white/5 px-4 py-2">
      <p className="text-xs text-syncchain-text-muted italic">{alt}</p>
    </div>
  </div>
);

/* ─── Collapsible Section ─── */
const Section = ({ icon: Icon, title, children, defaultOpen = false, img, imgAlt }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden bg-syncchain-paper/60 backdrop-blur-sm" data-testid={`help-section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-white/[0.03] transition-colors"
      >
        {Icon && <Icon className="w-5 h-5 text-syncchain-accent flex-shrink-0" strokeWidth={1.5} />}
        <span className="flex-1 font-semibold text-syncchain-text text-[15px]">{title}</span>
        {open ? (
          <ChevronDown className="w-4 h-4 text-syncchain-text-muted" />
        ) : (
          <ChevronRight className="w-4 h-4 text-syncchain-text-muted" />
        )}
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 text-sm text-syncchain-text-secondary leading-relaxed space-y-3 border-t border-white/5">
          {img && <Screenshot src={img} alt={imgAlt || title} />}
          {children}
        </div>
      )}
    </div>
  );
};

/* ─── FAQ Item ─── */
const FAQ = ({ question, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/5 last:border-0" data-testid={`faq-${question.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 py-3.5 text-left group"
      >
        <HelpCircle className="w-4 h-4 text-syncchain-accent flex-shrink-0" strokeWidth={1.5} />
        <span className="flex-1 text-syncchain-text font-medium text-sm group-hover:text-syncchain-accent transition-colors">
          {question}
        </span>
        {open ? (
          <ChevronDown className="w-3.5 h-3.5 text-syncchain-text-muted" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5 text-syncchain-text-muted" />
        )}
      </button>
      {open && (
        <div className="pl-6 pb-3.5 text-sm text-syncchain-text-secondary leading-relaxed">
          {children}
        </div>
      )}
    </div>
  );
};

/* ─── Step card ─── */
const Step = ({ num, title, children }) => (
  <div className="flex gap-3.5">
    <div className="w-7 h-7 rounded-full bg-syncchain-accent/15 flex items-center justify-center flex-shrink-0 mt-0.5">
      <span className="text-syncchain-accent font-bold text-xs">{num}</span>
    </div>
    <div className="flex-1">
      <h4 className="text-syncchain-text font-semibold text-sm mb-1">{title}</h4>
      <p className="text-syncchain-text-secondary text-sm leading-relaxed">{children}</p>
    </div>
  </div>
);

/* ─── Tip callout ─── */
const Tip = ({ children }) => (
  <div className="flex gap-2.5 bg-syncchain-accent/5 border border-syncchain-accent/15 rounded-lg px-4 py-3">
    <Info className="w-4 h-4 text-syncchain-accent flex-shrink-0 mt-0.5" strokeWidth={1.5} />
    <p className="text-sm text-syncchain-text-secondary leading-relaxed">{children}</p>
  </div>
);

/* ─── Image paths ─── */
const IMG = {
  dashboard: "/help-images/sc_dashboard.jpeg",
  search: "/help-images/sc_single_search.jpeg",
  recording: "/help-images/sc_recording.jpeg",
  publishing: "/help-images/sc_publishing.jpeg",
  shares: "/help-images/sc_pub_shares.jpeg",
  splits: "/help-images/sc_splits.jpeg",
  allSearches: "/help-images/sc_all_searches.jpeg",
  importCatalogue: "/help-images/sc_import_catalogue.jpeg",
  searchIsrc: "/help-images/sc_search_isrc.jpeg",
  searchSpotify: "/help-images/sc_search_spotify.jpeg",
  searchTitle: "/help-images/sc_search_title.jpeg",
  batchCsv: "/help-images/sc_batch_csv.jpeg",
  exportAll: "/help-images/sc_export_all.jpeg",
  exportArtist: "/help-images/sc_export_artist.jpeg",
  exportSong: "/help-images/sc_export_song.jpeg",
};

/* ─── Main Help Page ─── */
export default function HelpCenter() {
  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="help-center">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-syncchain-text tracking-tight">
          Help Centre
        </h1>
        <p className="mt-2 text-syncchain-text-secondary text-sm leading-relaxed max-w-xl">
          Everything you need to get started with SyncChain. Learn how to search,
          import catalogues, understand share data, and export your results.
        </p>
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { icon: Zap, label: "Getting Started", anchor: "getting-started" },
          { icon: Search, label: "Song Search", anchor: "single-search" },
          { icon: Upload, label: "Catalogue Import", anchor: "catalogue-import" },
          { icon: BarChart3, label: "Share Data", anchor: "share-data" },
        ].map((item) => (
          <button
            key={item.anchor}
            onClick={() => document.getElementById(item.anchor)?.scrollIntoView({ behavior: "smooth", block: "start" })}
            className="flex flex-col items-center gap-2 p-4 rounded-xl border border-white/10 bg-syncchain-paper/60 hover:border-syncchain-accent/30 hover:bg-white/[0.03] transition-all group"
            data-testid={`quick-link-${item.anchor}`}
          >
            <item.icon className="w-5 h-5 text-syncchain-text-muted group-hover:text-syncchain-accent transition-colors" strokeWidth={1.5} />
            <span className="text-xs font-medium text-syncchain-text-secondary group-hover:text-syncchain-text transition-colors">
              {item.label}
            </span>
          </button>
        ))}
      </div>

      {/* ─── GETTING STARTED ─── */}
      <div id="getting-started" className="scroll-mt-24 space-y-3">
        <h2 className="text-lg font-bold text-syncchain-text flex items-center gap-2">
          <Zap className="w-5 h-5 text-syncchain-accent" strokeWidth={1.5} />
          Getting Started
        </h2>

        <Section
          icon={BookOpen}
          title="What is SyncChain?"
          defaultOpen={true}
          img={IMG.dashboard}
          imgAlt="SyncChain dashboard — your centralised workspace for music rights lookup"
        >
          <p>
            SyncChain is a rights intelligence platform that resolves music ownership
            in seconds. Enter a song and get back the full ownership chain: recording
            metadata, writers, publishers, performing and mechanical share percentages,
            society affiliations, and IPI numbers.
          </p>
          <p>
            SyncChain aggregates data from multiple authoritative sources and presents
            it in a single, structured view — replacing hours of manual lookup across
            fragmented databases.
          </p>
        </Section>

        <Section
          icon={ArrowRight}
          title="Quick Start (3 minutes)"
          img={IMG.search}
          imgAlt="Single Song Search — paste a Spotify link, ISRC code, or enter title and artist"
        >
          <div className="space-y-4">
            <Step num="1" title="Run your first search">
              Navigate to <strong>Single Song Search</strong> in the sidebar. Paste a
              Spotify track link, enter an ISRC code, or type a title and artist. Hit search.
            </Step>
            <Step num="2" title="Review the results">
              SyncChain will match the recording and enrich it with publishing data.
              Results are organised across three tabs: <strong>Recording</strong>,{" "}
              <strong>Publishing</strong>, and <strong>Splits</strong>.
            </Step>
            <Step num="3" title="Export or edit">
              Download results as JSON or CSV for your workflow, or use the split editor
              to adjust share percentages before exporting.
            </Step>
          </div>
          <Tip>
            The enrichment pipeline runs automatically. PRO share data may take a few
            moments to populate — check the status indicator on the search result page.
          </Tip>
        </Section>
      </div>

      {/* ─── SINGLE SONG SEARCH ─── */}
      <div id="single-search" className="scroll-mt-24 space-y-3">
        <h2 className="text-lg font-bold text-syncchain-text flex items-center gap-2">
          <Search className="w-5 h-5 text-syncchain-accent" strokeWidth={1.5} />
          Single Song Search
        </h2>

        <Section
          icon={Music}
          title="Search by Spotify Link"
          img={IMG.searchSpotify}
          imgAlt="Spotify search tab — paste a Spotify track URL or track ID"
        >
          <p>
            Paste any Spotify track URL (e.g.{" "}
            <code className="text-xs bg-white/5 px-1.5 py-0.5 rounded text-syncchain-accent">
              https://open.spotify.com/track/...
            </code>
            ). SyncChain extracts the ISRC and full recording metadata directly from
            Spotify, then enriches it with publishing data from PRO databases.
          </p>
          <Tip>
            This is the recommended method when you have access to Spotify. The ISRC
            extraction ensures an exact match with no ambiguity.
          </Tip>
        </Section>

        <Section
          icon={Music}
          title="Search by ISRC Code"
          img={IMG.searchIsrc}
          imgAlt="ISRC search tab — enter an International Standard Recording Code for exact matching"
        >
          <p>
            Enter the International Standard Recording Code (e.g.{" "}
            <code className="text-xs bg-white/5 px-1.5 py-0.5 rounded text-syncchain-accent">
              GBAYE0601498
            </code>
            ). This is the most precise method — ideal when working from cue sheets,
            rights documentation, or distributor reports.
          </p>
          <p>
            SyncChain resolves the ISRC across music databases and returns verified
            metadata along with the full publishing ownership chain.
          </p>
        </Section>

        <Section
          icon={Music}
          title="Search by Title and Artist"
          img={IMG.searchTitle}
          imgAlt="Title/Artist search tab — enter track title and artist name"
        >
          <p>
            When you only have a song title and artist name, enter both fields.
            SyncChain searches across multiple databases to find the best match,
            ranked by confidence score.
          </p>
          <Tip>
            For the most accurate results, use the primary artist name as it appears
            on the recording (e.g. "Wu-Tang Clan" rather than individual member names).
          </Tip>
        </Section>
      </div>

      {/* ─── UNDERSTANDING RESULTS ─── */}
      <div id="results" className="scroll-mt-24 space-y-3">
        <h2 className="text-lg font-bold text-syncchain-text flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-syncchain-accent" strokeWidth={1.5} />
          Understanding Your Results
        </h2>

        <Section
          icon={Music}
          title="Recording Tab"
          img={IMG.recording}
          imgAlt="Recording tab — title, artist, ISRC, label, release date, album art, and confidence score"
        >
          <p>
            The Recording tab shows verified metadata for the matched recording:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 mt-2">
            <li>Title, primary artist, and featured artists</li>
            <li>ISRC (International Standard Recording Code)</li>
            <li>Release date, label, and album</li>
            <li>Duration and album artwork</li>
            <li>Recording credits where available (producers, engineers, musicians)</li>
          </ul>
          <p className="mt-2">
            A <strong>confidence score</strong> indicates how reliably SyncChain matched
            the recording. Scores above 80% indicate a strong match.
          </p>
        </Section>

        <Section
          icon={BarChart3}
          title="Publishing Tab"
          img={IMG.publishing}
          imgAlt="Publishing tab — ISWC, work title, writers and publishers with PRO affiliations"
        >
          <p>
            The Publishing tab contains the full ownership chain for the composition:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 mt-2">
            <li><strong>Work title</strong> and <strong>ISWC</strong> (International Standard Musical Work Code)</li>
            <li><strong>Writers</strong> — name, role (composer/author/arranger), IPI number, PRO affiliation</li>
            <li><strong>Publishers</strong> — name, IPI number, PRO affiliation, administration territory</li>
            <li><strong>Source badges</strong> indicating where each piece of data originated</li>
          </ul>
        </Section>

        <Section
          icon={BarChart3}
          title="Splits Tab &amp; Editor"
          img={IMG.splits}
          imgAlt="Splits tab — editable performing and mechanical percentages per party"
        >
          <p>
            The Splits tab is the editable workspace. SyncChain auto-populates writer
            and publisher splits from PRO data. You can:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 mt-2">
            <li>Review the auto-populated performing and mechanical percentages</li>
            <li>Edit shares independently using the dual-field split editor</li>
            <li>Verify totals before exporting</li>
          </ul>
        </Section>
      </div>

      {/* ─── SHARE DATA ─── */}
      <div id="share-data" className="scroll-mt-24 space-y-3">
        <h2 className="text-lg font-bold text-syncchain-text flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-syncchain-accent" strokeWidth={1.5} />
          Performing &amp; Mechanical Shares
        </h2>

        <Section
          icon={BarChart3}
          title="What are Performing and Mechanical shares?"
          defaultOpen={true}
          img={IMG.shares}
          imgAlt="Publishing tab showing individual performing and mechanical share percentages per writer and publisher, with share totals verified to 200%"
        >
          <p>
            When a musical work is created, two types of rights are generated:
          </p>
          <div className="mt-2 space-y-3">
            <div className="bg-white/[0.03] border border-white/5 rounded-lg p-3.5">
              <h4 className="text-syncchain-text font-semibold text-sm mb-1">Performing Rights</h4>
              <p className="text-sm">
                Generated when the work is performed publicly — played on radio, TV,
                streamed, performed live, or used in a public venue. Administered by
                performing rights organisations (PROs).
              </p>
            </div>
            <div className="bg-white/[0.03] border border-white/5 rounded-lg p-3.5">
              <h4 className="text-syncchain-text font-semibold text-sm mb-1">Mechanical Rights</h4>
              <p className="text-sm">
                Generated when the work is reproduced — pressed to vinyl, included
                on a CD, downloaded, or streamed. Administered by mechanical rights
                organisations or directly by publishers.
              </p>
            </div>
          </div>
          <p className="mt-3">
            Each writer and publisher has separate performing and mechanical percentages.
            Industry standard: <strong>100% performing + 100% mechanical = 200% total shares</strong>.
            SyncChain displays both separately because that's how they're licensed.
          </p>
        </Section>

        <Section icon={CheckCircle2} title="Why this data matters for licensing">
          <p>
            When clearing a track for sync (film, TV, advertising, gaming), you need to
            know exactly who controls the performing and mechanical rights and their
            respective shares. This determines:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 mt-2">
            <li>Who you need permission from to use the work</li>
            <li>How licensing fees are split between parties</li>
            <li>Which territories each party controls</li>
            <li>Whether all shares are accounted for (no gaps in clearance)</li>
          </ul>
          <p className="mt-2">
            SyncChain surfaces this data in one place so you can verify the full
            ownership chain before making licensing decisions.
          </p>
        </Section>
      </div>

      {/* ─── CATALOGUE IMPORT ─── */}
      <div id="catalogue-import" className="scroll-mt-24 space-y-3">
        <h2 className="text-lg font-bold text-syncchain-text flex items-center gap-2">
          <Upload className="w-5 h-5 text-syncchain-accent" strokeWidth={1.5} />
          Catalogue Import
        </h2>

        <Section
          icon={Upload}
          title="Spotify Playlist or Album Import"
          img={IMG.importCatalogue}
          imgAlt="Import Catalogue page — choose import type and preview tracks before processing"
        >
          <p>
            Navigate to <strong>Import Catalogue</strong> in the sidebar. Paste a
            Spotify playlist or album URL. SyncChain extracts every track from the
            collection and processes each one through the full enrichment pipeline.
          </p>
          <div className="space-y-4 mt-2">
            <Step num="1" title="Provide the URL">
              Paste any public Spotify playlist or album link. SyncChain automatically
              detects the type and extracts all tracks.
            </Step>
            <Step num="2" title="Automatic batch processing">
              Each track is queued and processed independently — recording identification,
              publishing resolution, and share extraction. Progress is tracked in real time.
            </Step>
            <Step num="3" title="Review and export">
              Completed tracks appear in <strong>All Searches</strong> with a catalogue
              badge. Browse individual results or export the entire batch as CSV.
            </Step>
          </div>
        </Section>

        <Section
          icon={FileSpreadsheet}
          title="Batch CSV Upload"
          img={IMG.batchCsv}
          imgAlt="Batch CSV page — upload a CSV file with ISRC, title, and artist columns for bulk processing"
        >
          <p>
            For catalogues without Spotify links, use <strong>Batch CSV</strong> in the
            sidebar. Upload a spreadsheet with columns for ISRC, title, and artist.
            SyncChain resolves each row and returns structured ownership data for the
            entire batch.
          </p>
          <p>
            Accepted columns: <code className="text-xs bg-white/5 px-1.5 py-0.5 rounded text-syncchain-accent">
            ISRC</code>, <code className="text-xs bg-white/5 px-1.5 py-0.5 rounded text-syncchain-accent">
            Title</code>, <code className="text-xs bg-white/5 px-1.5 py-0.5 rounded text-syncchain-accent">
            Artist</code>. At minimum, provide either an ISRC or a title + artist combination.
          </p>
        </Section>
      </div>

      {/* ─── EXPORTING ─── */}
      <div id="exporting" className="scroll-mt-24 space-y-3">
        <h2 className="text-lg font-bold text-syncchain-text flex items-center gap-2">
          <Download className="w-5 h-5 text-syncchain-accent" strokeWidth={1.5} />
          Exporting Data
        </h2>

        <Section
          icon={Download}
          title="Export all searches"
          img={IMG.exportAll}
          imgAlt="All Searches with no filter — CSV and JSON buttons export your entire catalogue"
        >
          <p>
            With no search term entered, the <strong>CSV</strong> and <strong>JSON</strong> buttons
            in the top-right will export every search in your library. Ideal for full catalogue backups
            or sending a complete dataset to a colleague.
          </p>
        </Section>

        <Section
          icon={Download}
          title="Export by artist"
          img={IMG.exportArtist}
          imgAlt="Searching 'Labrinth' filters to all tracks by that artist — export captures only those results"
        >
          <p>
            Type an artist name in the search bar to filter the list. The <strong>CSV</strong> and{" "}
            <strong>JSON</strong> export buttons will then only export the filtered results — giving
            you a clean export of every track by that artist.
          </p>
        </Section>

        <Section
          icon={Download}
          title="Export a single song"
          img={IMG.exportSong}
          imgAlt="Searching 'Jealous' filters to a single track — export captures just that song"
        >
          <p>
            Search for a specific song title to narrow the list to that track. Exporting
            now gives you a focused file with just that song's recording metadata, writers,
            publishers, and share percentages.
          </p>
        </Section>

        <Section icon={Download} title="Export formats">
          <p>SyncChain supports two export formats:</p>
          <div className="mt-2 space-y-3">
            <div className="bg-white/[0.03] border border-white/5 rounded-lg p-3.5">
              <h4 className="text-syncchain-text font-semibold text-sm mb-1">CSV</h4>
              <p className="text-sm">
                Flat spreadsheet format. Each row contains a split party with their
                performing and mechanical shares alongside the recording metadata.
                Ideal for importing into Excel, Google Sheets, or rights management systems.
              </p>
            </div>
            <div className="bg-white/[0.03] border border-white/5 rounded-lg p-3.5">
              <h4 className="text-syncchain-text font-semibold text-sm mb-1">JSON</h4>
              <p className="text-sm">
                Structured data format. Includes the full search result with nested
                recording, publishing, and splits data. Ideal for developers and
                system integrations.
              </p>
            </div>
          </div>
          <p className="mt-2">
            Export a single search from the search result page, or export multiple
            searches at once from <strong>All Searches</strong>.
          </p>
        </Section>
      </div>

      {/* ─── FAQS ─── */}
      <div id="faqs" className="scroll-mt-24 space-y-3">
        <h2 className="text-lg font-bold text-syncchain-text flex items-center gap-2">
          <HelpCircle className="w-5 h-5 text-syncchain-accent" strokeWidth={1.5} />
          Frequently Asked Questions
        </h2>

        <div className="border border-white/10 rounded-xl bg-syncchain-paper/60 backdrop-blur-sm px-5 py-2">
          <FAQ question="How does SyncChain find ownership data?">
            <p>
              SyncChain queries multiple authoritative music databases and PRO registries
              simultaneously, then cross-references and merges the results into a single
              structured view. The enrichment pipeline is fully automated.
            </p>
          </FAQ>

          <FAQ question="What is a confidence score?">
            <p>
              The confidence score (0–100%) indicates how reliably SyncChain matched your
              search query to a recording. It's calculated from factors like title similarity,
              artist match, and ISRC verification. Scores above 80% indicate a strong match.
            </p>
          </FAQ>

          <FAQ question="Why are some shares shown as 'not available'?">
            <p>
              Not all PRO databases provide performing and mechanical share breakdowns.
              When a writer or publisher is registered with a PRO that does not publish
              this data, SyncChain will indicate "shares not available from this source"
              rather than displaying incorrect information.
            </p>
          </FAQ>

          <FAQ question="Why didn't PRO splits load for my track?">
            <p>
              There are a few common reasons PRO share data may not load:
            </p>
            <p className="mt-2 font-medium text-syncchain-text">1. Brackets or extra text in the title</p>
            <p className="mt-1">
              PRO databases search by <strong>work title</strong>, not recording title. If
              your track title contains extra text in brackets — such as{" "}
              <code className="text-xs bg-white/5 px-1.5 py-0.5 rounded">
                All For Us (from the HBO Original Series Euphoria)
              </code>{" "}
              — the PRO lookup may fail because the registry only knows the work as{" "}
              <strong>"All For Us"</strong>. Remove everything in brackets from the title,
              then click <strong>Enrich</strong> to re-run the lookup.
            </p>
            <p className="mt-3 font-medium text-syncchain-text">2. Artist name differs from registered writers</p>
            <p className="mt-1">
              SyncChain matches PRO results by checking the <strong>performing artist name</strong> against
              the registered <strong>writers/creators</strong> on the work. If the artist performs under a
              stage name that doesn't appear in the PRO registration (e.g.{" "}
              <code className="text-xs bg-white/5 px-1.5 py-0.5 rounded">BIGXTHAPLUG</code>{" "}
              performing "The Largest", but the registered writers are under their legal names),
              SyncChain's confidence score may be too low to confirm a match. The work exists
              on the PRO registry but cannot be automatically verified as the correct one.
            </p>
            <p className="mt-3 font-medium text-syncchain-text">3. Suspense Account</p>
            <p className="mt-1">
              Some works show <strong>"Suspense Account — 100%"</strong> instead of
              actual writer/publisher shares. This means the royalty payments are being held by
              the PRO due to an ownership dispute, incorrect registration, or pending investigation.
              The shares are not yet assigned to the actual rights holders. SyncChain will display
              "shares not available" in these cases because there are no usable splits to import.
            </p>
            <p className="mt-3 font-medium text-syncchain-text">What you can do:</p>
            <ol className="list-decimal list-inside space-y-1 mt-1 ml-1 text-syncchain-text-secondary">
              <li>Clean up the title — remove brackets, subtitles, and version tags.</li>
              <li>Try the <strong>Enrich</strong> button after editing the title.</li>
              <li>If the work is in a suspense account, the shares will update automatically once the PRO resolves the dispute.</li>
            </ol>
          </FAQ>

          <FAQ question="No credits were found — how do I get PRO shares?">
            <p>
              SyncChain pulls writer/credit information from multiple databases before
              running PRO enrichment. However, some tracks — especially newer releases
              or certain genres — may not have credits registered on these databases yet.
            </p>
            <p className="mt-2">
              Without at least one writer name, the PRO enrichment cannot reliably match the correct work
              (many songs share the same title). This is a <strong>data gap</strong> in the
              upstream sources, not a bug.
            </p>
            <p className="mt-2 font-medium text-syncchain-text">How to fix this:</p>
            <ol className="list-decimal list-inside space-y-1 mt-1 ml-1 text-syncchain-text-secondary">
              <li>Look up one of the songwriters on Spotify (tap "Credits" on the track), Apple Music, or the PRO website directly.</li>
              <li>In SyncChain, go to the <strong>Recording</strong> tab and manually add the writer name under <strong>Credits</strong>.</li>
              <li>Click <strong>Enrich</strong> on the PRO data panel — SyncChain will retry the search using "Work Title + Writer Surname" and should find the correct work.</li>
            </ol>
            <p className="mt-2 text-syncchain-text-muted">
              You only need to add one writer name for this to work. SyncChain will display a
              hint on any track where PRO shares were not found and no credits are available.
            </p>
          </FAQ>

          <FAQ question="What does 200% total shares mean?">
            <p>
              In music publishing, performing rights and mechanical rights are tracked
              separately. Each totals 100%, so the combined total is 200%. This is the
              industry standard used by PROs worldwide — it is not an error.
            </p>
          </FAQ>

          <FAQ question="Can I edit the share percentages?">
            <p>
              Yes. The Splits tab includes a dual-field editor that allows you to adjust
              performing and mechanical percentages independently. Edits are saved to
              your search and reflected in exports.
            </p>
          </FAQ>

          <FAQ question="How long does enrichment take?">
            <p>
              Recording metadata (title, artist, ISRC) is returned in seconds. Publishing
              and share data from PRO sources may take up to 30 seconds depending on the
              complexity of the work and the number of interested parties.
            </p>
          </FAQ>

          <FAQ question="Can I search using an album or playlist link?">
            <p>
              Yes — use <strong>Import Catalogue</strong> in the sidebar. Paste a Spotify
              playlist or album URL and SyncChain will process every track in the collection
              through the full enrichment pipeline.
            </p>
          </FAQ>

          <FAQ question="What file format should my CSV be in?">
            <p>
              Standard CSV with column headers. Accepted columns:{" "}
              <code className="text-xs bg-white/5 px-1.5 py-0.5 rounded">ISRC</code>,{" "}
              <code className="text-xs bg-white/5 px-1.5 py-0.5 rounded">Title</code>,{" "}
              <code className="text-xs bg-white/5 px-1.5 py-0.5 rounded">Artist</code>.
              Provide at minimum either an ISRC or a title + artist combination per row.
            </p>
          </FAQ>

          <FAQ question="Can I restore a deleted search?">
            <p>
              Yes. Deleted searches are archived, not permanently removed. Navigate to{" "}
              <strong>All Searches</strong>, switch to the <strong>Archived</strong> tab,
              and click <strong>Restore</strong> on any search you want to bring back.
            </p>
          </FAQ>

          <FAQ question="Is there an API?">
            <p>
              API access is on our roadmap. If you're interested in integrating SyncChain's
              rights data into your own platform, please contact us at{" "}
              <a href="mailto:support@syncit.ac" className="text-syncchain-accent hover:underline">
                support@syncit.ac
              </a>{" "}
              to discuss early access.
            </p>
          </FAQ>
        </div>
      </div>

      {/* Bottom spacer */}
      <div className="h-8" />
    </div>
  );
}
